import React from 'react';
import styled from 'styled-components';
import { useDraggable } from '@dnd-kit/core';
import { CheckSquare, Square, GripVertical } from 'lucide-react';
import type { LeadCard, AIPriority } from '../../../../Data/leads.dto';
import {
  EVENT_TYPE_LABELS,
  SOURCE_LABELS,
} from '../../../../Data/leads.dto';
import {
  formatBudget,
  timeAgo,
  isStale,
  PRIORITY_COLORS,
} from './leadHelpers';

interface DraggableCardProps {
  lead: LeadCard;
  isSelected: boolean;
  selectMode: boolean;
  onCardClick: (id: string) => void;
  onCheckToggle: (id: string) => void;
  isDragActive: boolean;
  onSendQuotation?: (lead: LeadCard) => void;
  onUpdateQuotation?: (lead: LeadCard) => void;
  onSendOrder?: (lead: LeadCard) => void;
  isSharing?: boolean;
}

const CardWrap = styled.div<{ isDragging?: boolean; isStale?: boolean; isSelected?: boolean }>`
  position: relative;
  background: ${p => p.isSelected ? 'rgba(206, 86, 176, 0.04)' : 'white'};
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  box-shadow: ${p => p.isSelected ? '0 0 0 1px rgba(206, 86, 176, 0.2)' : '0 4px 14px rgba(0,0,0,0.05)'};
  border: 1px solid ${p => p.isSelected ? '#C94DAA' : 'rgba(0, 0, 0, 0.05)'};
  border-left: 3px solid ${p => p.isStale ? '#f59e0b' : p.isSelected ? '#C94DAA' : 'transparent'};
  opacity: ${p => p.isDragging ? 0.4 : 1};
  transition: box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
  width: 290px;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-select: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(0,0,0,0.08);
    border-color: rgba(201, 77, 170, 0.18);
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
`;

const CardName = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
`;

const PriorityBadge = styled.span<{ priority: AIPriority }>`
  font-family: 'Urbanist', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${p => PRIORITY_COLORS[p.priority].bg};
  flex-shrink: 0;
`;

const CardMeta = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  color: #6b7280;
  margin: 4px 0 0;
  line-height: 1.4;
`;

const CardBudget = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
`;

const CardMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const SourceTag = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
`;

const TimeTag = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  color: #9ca3af;
`;

const SendQuotationCardBtn = styled.button`
  width: 120px;
  margin-left: auto;
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendOrderCardBtn = styled.button`
  width: 120px;
  margin-left: auto;
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SelectCheckbox = styled.div<{ checked?: boolean }>`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  z-index: 2;
  background: white;
  border-radius: 4px;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: grab;
  padding: 4px 2px;
  border-radius: 4px;
  margin-right: 2px;
  
  &:hover {
    color: #4b5563;
    background: #f3f4f6;
  }
  &:active {
    cursor: grabbing;
  }
`;

const DraggableCard: React.FC<DraggableCardProps> = ({
  lead,
  isSelected,
  selectMode,
  onCardClick,
  onCheckToggle,
  isDragActive,
  onSendQuotation,
  onUpdateQuotation,
  onSendOrder,
  isSharing,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
  });

  const minFmt = formatBudget(lead.budget_min);
  const maxFmt = formatBudget(lead.budget_max);
  const budgetDisplay =
    maxFmt && minFmt !== maxFmt ? `${minFmt}–${maxFmt}` : minFmt || maxFmt;

  const eventDateFmt = lead.event_date
    ? new Date(lead.event_date).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
      })
    : null;

  const stale = isStale(lead.last_activity_at);

  return (
    <CardWrap
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      isDragging={isDragging}
      isStale={stale}
      isSelected={isSelected}
      onClick={(e) => {
        e.stopPropagation();
        if (selectMode) {
          onCheckToggle(lead.id);
        } else {
          onCardClick(lead.id);
        }
      }}
    >
      {selectMode && (
        <SelectCheckbox data-no-drag="true">
          {isSelected ? (
            <CheckSquare size={15} color="#C94DAA" />
          ) : (
            <Square size={15} color="#d1d5db" />
          )}
        </SelectCheckbox>
      )}
      <CardTopRow>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: 0,
            flex: 1,
          }}
        >
          {!selectMode && (
            <DragHandle data-drag-handle="true" {...listeners} {...attributes} onClick={(e) => e.stopPropagation()}>
              <GripVertical size={14} />
            </DragHandle>
          )}
          <CardName style={{ paddingLeft: selectMode ? 24 : 0 }}>{lead.name}</CardName>
        </div>
        {lead.ai_priority && (
          <PriorityBadge priority={lead.ai_priority}>
            {lead.ai_priority} ★
          </PriorityBadge>
        )}
      </CardTopRow>
      <CardMeta>
        {EVENT_TYPE_LABELS[lead.event_type] ?? lead.event_type}
        {eventDateFmt && ` · ${eventDateFmt}`}
      </CardMeta>
      {(lead.venue_city || lead.budget_min || lead.budget_max) && (
        <CardMetaRow>
          {lead.venue_city ? (
            <CardMeta style={{ margin: 0 }}>{lead.venue_city}</CardMeta>
          ) : (
            <div />
          )}
          {(lead.budget_min || lead.budget_max) && (
            <CardBudget>{budgetDisplay}</CardBudget>
          )}
        </CardMetaRow>
      )}
      <CardBottom>
        <SourceTag>{SOURCE_LABELS[lead.source] ?? lead.source}</SourceTag>
        <TimeTag>{timeAgo(lead.last_activity_at)}</TimeTag>
      </CardBottom>
      {lead.stage === 'CONTACTED' && onSendQuotation && (
        <SendQuotationCardBtn
          disabled={isSharing}
          onClick={(e) => {
            e.stopPropagation();
            onSendQuotation(lead);
          }}
        >
          {isSharing ? 'Sending...' : 'Send Quotation'}
        </SendQuotationCardBtn>
      )}
      {lead.stage === 'ADVANCE_PAID' && onSendOrder && (
        <SendOrderCardBtn
          disabled={isSharing}
          onClick={(e) => {
            e.stopPropagation();
            onSendOrder(lead);
          }}
        >
          {isSharing ? 'Sending...' : 'Send Order'}
        </SendOrderCardBtn>
      )}
      {lead.stage === 'NEGOTIATING' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', marginTop: '10px' }} onClick={(e) => e.stopPropagation()}>
          {onUpdateQuotation && (
            <SendQuotationCardBtn
              disabled={isSharing}
              onClick={(e) => {
                e.stopPropagation();
                onUpdateQuotation(lead);
              }}
              style={{ marginTop: 0 }}
            >
              Update Quotation
            </SendQuotationCardBtn>
          )}
          {onSendQuotation && (
            <SendQuotationCardBtn
              disabled={isSharing}
              onClick={(e) => {
                e.stopPropagation();
                onSendQuotation(lead);
              }}
              style={{ marginTop: 0 }}
            >
              {isSharing ? 'Sending...' : 'Send Quotation'}
            </SendQuotationCardBtn>
          )}
        </div>
      )}
    </CardWrap>
  );
};

export default DraggableCard;
